import React, {
  ForwardRefRenderFunction,
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import _ from "lodash";
import { useTranslation } from "react-i18next";

import { CartonView } from "../../../../../models/carton";
import { CartonLog, CartonLogReason, CartonLogType } from "../../../../../models/carton.log";
import env from "../../../../../util/env";
import readCSV from "../../../../../util/readCSV";
import { useBookingContext } from "../../../../Booking/context/Booking";
import { NotificationToast } from "../../../Toast/NotificationToast";
import UploadCSVFile, { UploadCSVErrors } from "../../../UploadCSVFile";
import { ValidateCSVFieldErrors } from "../../../UploadCSVFile/types";
import { CartonReceiveTemplateCSV } from "../../util";
import * as ValidateCSV from "./csv.validate";

interface UploadCSVReceivedSSCCProps {
  ref: RefObject<UploadCSVFileHandles>;
  updateCartons?: (cartons: CartonView[]) => void;
  cartons?: CartonView[];
  bookingId?: number;
}

export interface UploadCSVFileHandles {
  open: () => void;
  close: () => void;
}

const UploadCSVReceivedSSCC: ForwardRefRenderFunction<
  UploadCSVFileHandles,
  UploadCSVReceivedSSCCProps
> = (props, ref) => {
  const { updateCartons, cartons, bookingId } = props;
  const { setCartonLogs } = useBookingContext();

  const modalRef = useRef<UploadCSVFileHandles>(null);

  const { t: translate } = useTranslation();
  const [lineErrorsCSV, setLineErrorsCSV] = useState<UploadCSVErrors>({});
  const [generalErrorsCSV, setGeneralErrorsCSV] = useState<UploadCSVErrors>({});

  const openModal = useCallback(() => {
    modalRef.current?.open();
  }, []);
  const closeModal = useCallback(() => {
    modalRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));
  const addBracketsFromCartonSSCC = (sscc: string) => {
    if (!sscc) return sscc;
    if (sscc.startsWith("(00)")) return sscc;
    if (sscc.startsWith("00")) {
      return sscc
        .replace(/\D/g, "") // remove all non-numeric value
        .replace(/(\d{2})(\d)/, "($1)$2"); // get the firsts digits and add brackets e.g 0012345 > (00)12345
    }

    return sscc;
  };

  const warningInvalidSSCCs = (invalidsCount: number) =>
    `${translate("attention")}: ${invalidsCount} ${translate(
      "boxesCouldntBeMarkedAsReceivedPleaseCheckAllErrorsOnWarehouseScanDataDocumentAndTryAgain"
    )}`;

  const receiveSSCCNotifications = (cartonNewLogs: CartonLog[], invalidSSCCCount: number) => {
    if (!invalidSSCCCount) {
      NotificationToast.success(translate("allSSCCsFromCsvWereMarkedAsReceived"));
    } else if (invalidSSCCCount === cartonNewLogs.length) {
      NotificationToast.error(
        translate(
          "NoneSSCCsCouldBeMarkedAsReceivedPleaseCheckAllErrorsOnWarehouseScanDataDocumentAndTryAgain"
        ),
        { duration: 3000 }
      );
    } else if (invalidSSCCCount < cartonNewLogs.length) {
      NotificationToast.warning(warningInvalidSSCCs(invalidSSCCCount), { duration: 6000 });
    }
  };

  const onCloseSSCCs = (cartonsToReceive: string[]) => {
    if (!cartonsToReceive.length || !updateCartons || !cartons?.length || !bookingId) return;
    const cartonNewLogs: CartonLog[] = [];
    const cloned = _.cloneDeep(cartons);
    let invalidSSCCCount = 0;

    cartonsToReceive.forEach((cartonReceive) => {
      const formatedCartonsToReceive = addBracketsFromCartonSSCC(cartonReceive);
      const foundCartonIndex = cloned?.findIndex(
        (carton) => carton.sscc === formatedCartonsToReceive
      );
      const baseNewLog = {
        sscc: cartonReceive,
        shipmentId: bookingId,
      };

      if (foundCartonIndex === -1) {
        invalidSSCCCount += 1;
        cartonNewLogs.push({
          ...baseNewLog,
          type: CartonLogType.ERROR,
          reason: translate(CartonLogReason.NOT_FOUND),
          isReceived: false,
        });
      } else {
        cartonNewLogs.push({
          ...baseNewLog,
          type: CartonLogType.SUCCESS,
          reason: translate(CartonLogReason.FOUND),
          isReceived: true,
        });
        cloned[foundCartonIndex].isReceivedByCfs = true;
      }
    });
    setCartonLogs((oldCartonLogs) => [...oldCartonLogs, ...cartonNewLogs]);
    updateCartons(cloned);
    receiveSSCCNotifications(cartonNewLogs, invalidSSCCCount);
    closeModal();
  };

  const onConfirmUploadCSV = async (data: CartonReceiveTemplateCSV[]) => {
    if (!data || !data.length) {
      NotificationToast.warning(translate("thereAreNoRecordsInThisFile"), {
        id: "warning-file-empty",
      });
    }
    if (!data || !data.length) return;

    const lineErrors: ValidateCSVFieldErrors = {};
    const csvGlobalErrors: UploadCSVErrors = {};

    const { valid } = ValidateCSV.validateCSVFields(data, lineErrors, translate);

    if (valid) {
      onCloseSSCCs(data.map((item) => item.sscc));
    }

    if (!valid || !!Object.keys(csvGlobalErrors).length) {
      setLineErrorsCSV(lineErrors);
      setGeneralErrorsCSV(csvGlobalErrors);
    }
  };

  return (
    <UploadCSVFile
      ref={modalRef}
      onConfirmUpload={(uri) => {
        readCSV<CartonReceiveTemplateCSV>(uri, ["sscc"], (data) => onConfirmUploadCSV(data), {
          uuid: true,
        });
      }}
      lineErrors={lineErrorsCSV}
      generalErrors={generalErrorsCSV}
      onCloseModal={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
      }}
      onDownloadTemplate={() =>
        window.open(env.LINK_TEMPLATE_RECEIVE_CARTONS_CSV, "_blank")?.focus()
      }
    />
  );
};

export default React.forwardRef(UploadCSVReceivedSSCC);
